import type { StaticEntityType } from "@config";
import { usePdfDetails } from "@library/components";
import { useMemo } from "react";
import { QrBox, QrWrapper } from "../styles/QrCode.styled";
import type { EditorDataProps, EditorSlotsProps, QrCodeProps } from "../types";
import {
  getRuntimeVersionUrl,
  isClickableHref,
  resolveQrValue,
} from "../utils/resolve-qr-value";
import { QrCodeSvg } from "./QrCodeSvg";

export const QR_DEFAULT_SIZE = 160;

export const QrCodeLibrary = ({
  entity,
  editorData,
  editorSlots,
}: {
  entity: StaticEntityType;
  editorData?: EditorDataProps;
  editorSlots?: EditorSlotsProps;
}) => {
  const { settings } = entity as StaticEntityType<QrCodeProps>;
  const { isPdf, shortUrl } = usePdfDetails();
  const isEditor = !!editorData;

  // The editor resolves the value itself (version data, the sender's profile,
  // a stand-in for links that only exist once a version is created). A live
  // deck reads what the backend baked into the settings.
  const liveValue = useMemo(
    () =>
      isEditor
        ? null
        : resolveQrValue(settings, () =>
            getRuntimeVersionUrl(isPdf ? shortUrl : undefined),
          ),
    [isEditor, settings, isPdf, shortUrl],
  );
  const value = isEditor ? editorData.value : liveValue;
  if (!value) return null;

  const size = Number(editorData?.size || settings.size) || QR_DEFAULT_SIZE;
  const clickable =
    !isEditor && settings.clickable !== false && isClickableHref(value);

  const code = (
    <QrBox className="qr-code-box tw-relative" style={{ width: `${size}px` }}>
      {editorSlots?.resizeSlot}
      {/* The drag trigger is the code, not the box: the box also holds the
          resize handles, and a press on one must not start a move. */}
      <div
        className="qr-code-art tw-relative tw-aspect-square tw-w-full"
        {...(isEditor && { "data-dnd-trigger": "true" })}
      >
        <QrCodeSvg
          value={value}
          color={settings.color}
          backgroundColor={settings.backgroundColor}
          dotStyle={settings.dotStyle || "square"}
          cornerStyle={settings.cornerStyle || "square"}
          centerType={settings.centerType || "none"}
          centerImage={settings.centerImage}
          centerIcon={settings.centerIcon}
          centerSize={settings.centerSize}
        />
      </div>
      {/* Editor-only; floats under the code (the box is its anchor) so
          showing it never changes the element's height. */}
      {editorSlots?.badgeSlot}
    </QrBox>
  );

  return (
    <QrWrapper $horizontalAlignment={settings.horizontalAlignment}>
      {clickable ? (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${value}`}
          className="tw-flex !tw-no-underline [justify-content:inherit]"
        >
          {code}
        </a>
      ) : (
        code
      )}
    </QrWrapper>
  );
};
